import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Heading from '../components/Heading'
import ModuleWithLoader from '../components/ModuleWithLoader'
import DataSource from '../DataSource'
import SubHeader from '../components/SubHeader'
import MasterSwitchForm from '../forms/MasterSwitchForm'
import DisabledOverlay from '../components/DisabledOverlay'
import globals from '../globals'
import QuietTimeSchedulingForm from '../forms/QuietTimeSchedulingForm'
import QuietTimeOverrideForm from '../forms/QuietTimeOverrideForm'
import ParentalControlTable from '../components/ParentalControlTable'
import QuietTimeSchedule from '../components/QuietTimeSchedule'
import LoadingOverlay from '../components/LoadingOverlay'
import { useMedia } from 'react-use'

const CATEGORY_TYPE_PARENTAL = 'parentalContent'
const CATEGORY_TYPE_GENERAL = 'generalContent'

const ParentalControl = (props) => {
    const isMobile = useMedia('(max-width: 992px)')

    const [hasError, setHasError] = useState(false)
    const [parentalCategories, setParentalCategories] = useState([])
    const [generalCategories, setGeneralCategories] = useState([])
    const [masterIsEnabled, setMasterIsEnabled] = useState(props.serviceDetails.parentalControlEnabled)
    const [overrideQuietTimeTimerEnds, setOverrideQuietTimeTimerEnds] = useState(props.serviceDetails.overrideQuietTimeTimerEnds)
    const [scheduleData, setScheduleData] = useState({
        isEnabled: props.serviceDetails.scheduleEnabled,
        items: props.serviceDetails.schedule
    })
    const [isLoadingOverrideData, setIsLoadingOverrideData] = useState(false)
    const [isLoadingScheduleData, setIsLoadingScheduleData] = useState(false)
    const [isLoadingCategories, setIsLoadingCategories] = useState(true)
    const [isLoadingCategoryChange, setIsLoadingCategoryChange] = useState(false)
    const [hasCategoryError, setHasCategoryError] = useState(false)
    const [timer, setTimer] = useState(setTimeout(() => {}, 0))

    useEffect(() => {
        DataSource.getData(`/api/getCategories/homeSecure/${props.serviceId}`, response => {
            setIsLoadingCategories(false)
            setParentalCategories(response.parental)
            setGeneralCategories(response.general)
        }, () => {
            setHasError(true)
            setIsLoadingCategories(false)
        })
    }, [])

    const handleToggleSchedule = isEnabled => {
        setIsLoadingScheduleData(true)
        const data = {
            ...scheduleData,
            isEnabled: isEnabled,
            serviceId: props.serviceId
        }

        DataSource.postData('/api/postToggleQuietTimeSchedule', data, response => {
            // todo: Might be nice to show the user a success tick or similar
            setScheduleData(data)
            setIsLoadingScheduleData(false)
            props.updateServiceDetails({ scheduleEnabled: isEnabled })
            globals.pushEvent(isEnabled ? 'HsEnableQuietTimeMasterOn' : 'HsEnableQuietTimeMasterOff')
        }, response => {
            setHasError(true)
            setIsLoadingScheduleData(false)
            globals.handleError(response.message, response)
        })
    }

    const handleDeleteScheduleItem = itemKey => {
        setIsLoadingScheduleData(true)
        const data = [...scheduleData.items]
        data.splice(itemKey, 1)

        DataSource.postData('/api/postDeleteQuietTimeSchedule', {
            serviceId: props.serviceId,
            scheduleItems: data
        }, response => {
            setIsLoadingScheduleData(false)
            setScheduleData({ ...scheduleData, items: data })
            props.updateServiceDetails({ schedule: data })
            globals.pushEvent('HsFocusTimeScheduleDelete')
        }, response => {
            setHasError(true)
            setIsLoadingScheduleData(false)
            globals.handleError(response.message, response)
        })
    }

    const handleCancelOverrideTimer = () => {
        setIsLoadingOverrideData(true)
        DataSource.postData('/api/postCancelQuietTimeOverride', {
            serviceId: props.serviceId
        }, response => {
            setOverrideQuietTimeTimerEnds(null)
            setIsLoadingOverrideData(false)
            props.updateServiceDetails({ overrideQuietTimeTimerEnds: null })
            globals.pushEvent('HsFocusTimeNowCancel')
        }, response => {
            setHasError(true)
            setIsLoadingOverrideData(false)
            globals.handleError(response.message, response)
        })
    }

    const handleQuietTimeOverrideSuccess = (response, values) => {
        if (response.data && response.data.timerEnds) {
            setOverrideQuietTimeTimerEnds(response.data.timerEnds)
            globals.pushEvent('HsFocusTimeNowStart')
        }
    }

    const handleSetCategories = (dataArr, type, noTimer = false) => {
        return new Promise((resolve, reject) => {
            setHasCategoryError(false)
            let data; const dataFiltered = []
            if (type === CATEGORY_TYPE_PARENTAL) {
                data = [...dataArr, ...generalCategories]
            } else {
                data = [...dataArr, ...parentalCategories]
            }

            type === CATEGORY_TYPE_PARENTAL ? setParentalCategories(dataArr) : setGeneralCategories(dataArr)

            clearTimeout(timer)
            setTimer(setTimeout(() => {
                setIsLoadingCategoryChange(true)
                setHasCategoryError(false)

                data.forEach(category => {
                    dataFiltered.push({
                        categoryId: category.categoryId,
                        currentlyBlocked: category.currentlyBlocked,
                        quietTimeBlocked: category.quietTimeBlocked
                    })
                })

                DataSource.postData('/api/postUpdateParentalCategories', {
                    categories: dataFiltered,
                    serviceId: props.serviceId
                }, response => {
                    setIsLoadingCategoryChange(false)
                    resolve()
                }, response => {
                    setIsLoadingCategoryChange(false)
                    setHasCategoryError(true)
                    globals.handleError(response.message, response)
                    reject()
                })
            }, isMobile || noTimer ? 0 : 1000))
        })
    }

    const handleAddSchedule = event => {
        event.preventDefault()
        const items = [...scheduleData.items, {
            startTime: '15:00',
            endTime: '17:00',
            days: [],
            isNew: true
        }]

        setScheduleData({ ...scheduleData, items: items })
    }

    const handleScheduleUpdateSuccess = response => {
        const newItems = []
        response.data.items.forEach(item => {
            newItems.push({ ...item, isNew: false })
        })

        setScheduleData({ ...scheduleData, items: newItems })
        props.updateServiceDetails({ schedule: newItems })
    }

    const handleChangeMasterSwitch = value => {
        setMasterIsEnabled(value)
        globals.pushEvent(value ? 'HsParentalControlToggleOn' : 'HsParentalControlToggleOff')
        props.updateServiceDetails({ parentalControlEnabled: value })
    }

    return (
        <div className='module overflow-visible'>
            <Heading title='Parental control & Focus-time' />
            <ModuleWithLoader isLoaded hasError={hasError} className='px-2 py-4 p-lg-4'>
                <div className='row mb-2'>
                    <div className='col-12 col-md-10 offset-md-1 text-center'>
                        <SubHeader
                            content="As Home Secure works at the level of your internet connection, you can quickly and easily set or adjust your Parental Control settings to help block inappropriate content. We’ve grouped the most contentious and distracting websites under <span class='bold'>Parental Control Categories</span> so you can quickly review the list and make a decision about which categories you’d like to block, and which you’re happy to allow. A further list of more family friendly <span class='bold'>General Content Categories</span> can also be managed if you so desire."
                            className='mb-5'
                        />
                        <MasterSwitchForm
                            key={masterIsEnabled}
                            serviceId={props.serviceId}
                            isEnabled={masterIsEnabled}
                            postUrl='/api/postToggleParentalControl'
                            title='Parental Control Master Switch'
                            subTitle='Disabling will leave your internet connection unfiltered and Focus-time will not work'
                            type='Parental Control'
                            handleChange={handleChangeMasterSwitch}
                        />
                    </div>
                </div>
                <div className='row mb-4'>
                    <div className='col-12 col-md-10 offset-md-1'>
                        <DisabledOverlay isDisabled={!masterIsEnabled}>
                            <div className='row'>
                                <div className='col-12 col-md-6 mb-4 mb-lg-0'>
                                    <QuietTimeSchedule
                                        isLoading={isLoadingScheduleData}
                                        isEnabled={scheduleData.isEnabled}
                                        handleUpdate={handleToggleSchedule}
                                    >
                                        <div>
                                            {scheduleData.items.map((item, key) => {
                                                if (!scheduleData.isEnabled) {
                                                    return null
                                                }

                                                const fields = {
                                                    serviceId: props.serviceId,
                                                    startTime: item.startTime,
                                                    endTime: item.endTime,
                                                    days: item.days
                                                }

                                                return (
                                                    <QuietTimeSchedulingForm
                                                        key={key}
                                                        isLoading={isLoadingScheduleData}
                                                        isEnabled={scheduleData.isEnabled}
                                                        isNew={item.isNew}
                                                        items={scheduleData.items}
                                                        itemKey={key}
                                                        fields={fields}
                                                        handleDeleteItem={handleDeleteScheduleItem}
                                                        handleSuccess={handleScheduleUpdateSuccess}
                                                    />
                                                )
                                            })}
                                        </div>
                                        {scheduleData.isEnabled && (scheduleData.items.length === 0 || scheduleData.items.length < 3)
                                            ? <div className='text-center mt-4'>
                                                <a href='#' className='font-small' onClick={handleAddSchedule}>Add a schedule</a>
                                              </div>
                                            : ''}
                                    </QuietTimeSchedule>
                                </div>
                                <div className='col-12 col-md-6'>
                                    <QuietTimeOverrideForm
                                        isLoading={isLoadingOverrideData}
                                        timerEnds={overrideQuietTimeTimerEnds}
                                        handleCancel={handleCancelOverrideTimer}
                                        fields={{
                                            serviceId: props.serviceId,
                                            hours: null,
                                            minutes: null
                                        }}
                                        handleSuccess={handleQuietTimeOverrideSuccess}
                                    />
                                </div>
                            </div>
                        </DisabledOverlay>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-12 col-md-10 offset-md-1'>
                        <LoadingOverlay isLoading={isLoadingCategories}>
                            <DisabledOverlay isDisabled={!masterIsEnabled || isLoadingCategoryChange}>
                                <div className='mb-4'>
                                    <ParentalControlTable
                                        title={<span>Parental control categories<span className='font-small regular'> - check the content you'd like to block</span></span>}
                                        categories={parentalCategories}
                                        handleSetCategories={handleSetCategories}
                                        type={CATEGORY_TYPE_PARENTAL}
                                        expandable
                                        hasError={hasCategoryError}
                                        isLoading={isLoadingCategoryChange}
                                    />
                                </div>
                                <ParentalControlTable
                                    title={<span>General content categories<span className='font-small regular'> - these website categories are more family friendly</span></span>}
                                    categories={generalCategories}
                                    handleSetCategories={handleSetCategories}
                                    type={CATEGORY_TYPE_GENERAL}
                                    expandable
                                    hasError={hasCategoryError}
                                    isLoading={isLoadingCategoryChange}
                                />
                            </DisabledOverlay>
                        </LoadingOverlay>
                    </div>
                </div>
            </ModuleWithLoader>
        </div>
    )
}

ParentalControl.propTypes = {
    serviceId: PropTypes.number.isRequired,
    serviceDetails: PropTypes.shape({
        parentalControlEnabled: PropTypes.bool,
        scheduleEnabled: PropTypes.bool,
        schedule: PropTypes.array,
        overrideQuietTimeTimerEnds: PropTypes.string
    }).isRequired,
    updateServiceDetails: PropTypes.func.isRequired
}

export default ParentalControl



// WEBPACK FOOTER //
// ./resources/js/modules/ParentalControl.js